import { SupportedLanguages as SupportedLanguagesFromPackage } from '@repo/types/common';
import { WidgetStudy } from '@repo/types/models';
import type { Ical, StepT } from '../steps/types';
import api from './api';

export type WidgetTerms = {
    id: string;
    shortText: string;
    legalName: string;
};

export type SupportedLanguages = SupportedLanguagesFromPackage;

export type Study = WidgetStudy;

export type StudyActive = {
    active: boolean;
    language: string;
};

export function reschedule(
    study: Study,
    { startDate, sessionId }: { startDate: string; sessionId: string },
) {
    return api.put(`/v2/study/id/${study.id}/reschedule`, {
        body: JSON.stringify({ startDate, sessionId }),
    });
}

export type BookingConfirmation = {
    consentId: string;
    slotId: string;
    ical: Ical;
};

export type BookingStep = {
    step: StepT & { slotLength: Study['slotLength'] };
    legal: WidgetTerms;
};

type PostAnswerResponse =
    | void
    | BookingConfirmation
    | { step: StepT; legal: WidgetTerms }
    | { isQualifying: boolean };

export async function postAnswer(
    step: StepT,
    studyId: string,
    sessionID: string,
    demoMode: boolean,
): Promise<PostAnswerResponse> {
    if (!step.answer) {
        throw new Error('No answer'); // TODO handle and log this
    }

    let body = {};
    switch (step.type) {
        case 'studyPanel':
            body = {
                type: step.type,
                answer: step.answer,
                question: step.label,
                acceptedTerms: step.answer.acceptedTerms,
                sessionID,
                reference: step.id,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                noSuitableTimes: step.noSuitableTimes,
            };
            break;
        case 'calendarStep':
            body = {
                type: 'calendarStep',
                answer: step.answer.startDate,
                email: step.answer.email,
                phone: step.answer.phoneNumber,
                acceptedTerms: step.answer.acceptedTerms,
                question: step.label,
                reference: step.id,
                sessionID,
                panelId: step.answer.panelId,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            };
            break;
        case 'multipleChoice':
        case 'statement':
        case 'checkboxes':
            body = {
                type: step.type,
                answer: step.answer,
                question: step.label,
                sessionID,
                reference: step.id,
            };
            break;
        case 'noSuitableTimes':
            body = {
                type: step.type,
                answer: step.answer,
            };
            break;
        default:
            throw new Error(`We don't support ${JSON.stringify(step)}`);
    }
    const res = (await api.post(`/v2/study/id/${studyId}/question/response`, {
        body: JSON.stringify({ ...body, demoMode }),
    })) as PostAnswerResponse;
    return res;
}
